'use client'

import { useRef, useState } from 'react'
import { useTranslations } from 'next-intl'
import { useRouter } from '@bprogress/next/app'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  ProductPictureImportForm,
  type ProductPictureImportFormRef
} from '@/components/partials/products/picture-imports'
import { type CreateProductPictureImportParams } from '@/types/product-picture-import'
import { useCreateProductPictureImport } from '@/hooks'
import { FormModeEnum } from '@/enums'

const Create = () => {
  const t = useTranslations()
  const router = useRouter()
  const queryClient = useQueryClient()
  const productPictureImportFormRef = useRef<ProductPictureImportFormRef>(null)
  const [isRedirecting, setIsRedirecting] = useState(false)
  const { mutate: createProductPictureImport, isPending: isLoading } = useCreateProductPictureImport()

  const handleSubmit = (data: CreateProductPictureImportParams) => {
    createProductPictureImport(data, {
      onSuccess: async newProductPictureImport => {
        setIsRedirecting(true)
        // Set the data in a cache with the same staleTime as the query to prevent immediate refetch
        queryClient.setQueryData(['productPictureImport', newProductPictureImport.id], newProductPictureImport)
        // Prefetch to ensure the query is in a success state
        queryClient.setQueryDefaults(['productPictureImport', newProductPictureImport.id], {
          staleTime: 5 * 60 * 1000
        })
        await queryClient.invalidateQueries({ queryKey: ['productPictureImports'] })
        toast.success(t('created_successfully'))
        router.push(`/products/import/picture/${newProductPictureImport.id}`)
      },
      onError: handleError
    })
  }

  const handleError = (error: any) => {
    if (error.response?.status === 422 || error.isValidationError) {
      if (productPictureImportFormRef.current) {
        productPictureImportFormRef.current.handleValidationErrors(error)
      }
      return
    }
    toast.error(t('error_creating'))
  }

  const handleCancel = () => {
    router.push('/products/import/picture')
  }

  return (
    <ProductPictureImportForm
      ref={productPictureImportFormRef}
      mode={FormModeEnum.CREATE}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      isLoading={isLoading || isRedirecting}
    />
  )
}

export default Create
